/**
 * `Idempotency-Key` ของการรับเข้าสต๊อก — **หนึ่งใบรับ หนึ่งคีย์**
 *
 * นี่คือสาระของหน้าจอรับเข้า ไม่ใช่ header ที่เติมให้ครบ: `request_dedup` ฝั่ง
 * เซิร์ฟเวอร์มีไว้เพื่อให้เครื่องที่เน็ตหลุดแล้วกดซ้ำ **ไม่ได้ lot สองใบ** และ
 * client ที่สร้างคีย์ใหม่ทุกครั้งที่กดปุ่มคือ client ที่ทำลายกลไกนั้นทิ้งทั้งหมด
 * — สองครั้งที่กดคือเหล็กสองกอง และสองแถวในบัญชี
 *
 * กติกาที่ไฟล์นี้บังคับ:
 *
 * - **retry ของใบเดิม = คีย์เดิม** ผูกคีย์ไว้กับ *เนื้อของคำขอ* ที่กำลังจะส่ง
 *   ไม่ใช่กับจำนวนครั้งที่กด
 * - **ใบใหม่ = คีย์ใหม่** ทั้งกรณีที่ผู้ใช้แก้ค่าในฟอร์มก่อนกดใหม่ และกรณีที่ใบ
 *   ก่อนหน้าสำเร็จไปแล้ว (ผู้เรียกทิ้ง submission เดิมด้วย `null`)
 *
 * ที่ผูกกับเนื้อของคำขอแทนที่จะ "เก็บคีย์ไว้จนกว่าจะสำเร็จ" เพราะอย่างหลังจะยิง
 * คีย์เดิมไปกับ body ที่ต่างออกไปทันทีที่ผู้ใช้แก้จำนวนแล้วกดใหม่ ซึ่ง contract
 * ตอบ `409 IDEMPOTENCY_KEY_REUSED` — เป็นความล้มเหลวที่หน้าจอสร้างขึ้นเอง
 * ทั้งที่ผู้ใช้ไม่ได้ทำอะไรผิด
 */

/** คำขอหนึ่งครั้งที่ถูกส่งออกไปแล้ว — เนื้อของมัน และคีย์ที่มันถือ */
export interface ReceiptSubmission {
  /**
   * ลายนิ้วมือของ body ที่ถูก serialise แล้ว — เทียบด้วยสตริง ไม่ใช่การเทียบ
   * object ทีละคีย์ เพราะสิ่งที่ต้องเหมือนกันคือ **ไบต์ที่ส่งออกไปจริง** ซึ่งเป็น
   * สิ่งเดียวกับที่ `request_dedup.request_hash` ฝั่งเซิร์ฟเวอร์เปรียบเทียบ
   */
  readonly fingerprint: string;
  readonly key: string;
}

/**
 * สร้างคีย์ใหม่จากแหล่งสุ่มเชิงเข้ารหัสเท่านั้น
 *
 * คีย์ที่ชนกันแปลว่าใบรับสองใบถูกมองเป็นใบเดียว ของจริงที่เข้ามากองที่สองจะ
 * หายไปเงียบ ๆ จึงยอมให้ล้มเหลวดัง ๆ ดีกว่าถอยไปใช้ `Math.random()`
 *
 * ⚠️ ไม่ได้ import `generateClientEventId()` จาก `lib/offline/commandQueue.ts`
 * ทั้งที่กติกาการสุ่มเหมือนกัน เพราะโมดูลนั้นเป็นคิวออฟไลน์ของเครื่องสแกน: มันดึง
 * IndexedDB ทั้งชุดเข้ามาใน bundle ของ Desktop และโยน `OfflineQueueError` ที่ผูก
 * กับการเขียนคิว ส่วนค่าที่มันสร้างคือ `client_event_id` ของ Offline Sync ซึ่งเป็น
 * คนละสิ่งกับ `Idempotency-Key` ของ HTTP ที่นี่ — ถ้าวันหนึ่งทั้งสองที่ต้องใช้
 * ตัวสร้างตัวเดียวกัน มันควรถูกยกขึ้นไปเป็น helper กลาง ไม่ใช่ให้ฝั่งหนึ่ง import
 * ข้ามเข้าไปในคิวของอีกฝั่ง
 */
export function newIdempotencyKey(): string {
  const webCrypto = (globalThis as { crypto?: Crypto }).crypto;

  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');
  }

  throw new Error('เครื่องนี้ไม่มีแหล่งสุ่มที่ปลอดภัยพอจะสร้าง Idempotency-Key');
}

/**
 * คีย์ที่คำขอครั้งถัดไปต้องใช้
 *
 * คืน submission เดิมทั้งก้อนเมื่อเนื้อของคำขอไม่เปลี่ยน — นั่นคือ "กดซ้ำหลังจาก
 * ล้มเหลว" ซึ่งต้องเป็น **คำขอเดียวกัน** ในสายตาเซิร์ฟเวอร์ ไม่ใช่ใบที่สอง
 *
 * `generate` รับเข้ามาเพื่อให้เทสต์พิสูจน์ได้ว่าคีย์ถูกสร้างกี่ครั้งและเมื่อไร
 * โดยไม่ต้องเดาจากค่าที่สุ่มออกมา
 */
export function submissionFor(
  previous: ReceiptSubmission | null,
  fingerprint: string,
  generate: () => string = newIdempotencyKey,
): ReceiptSubmission {
  if (previous !== null && previous.fingerprint === fingerprint) {
    return previous;
  }
  return { fingerprint, key: generate() };
}
